// Flash.js
/**
 * Flash
 * -----
 * Helper service used to dispatch UI flash notifications across the application.
 *
 * This class provides a simple API for triggering feedback messages without
 * directly interacting with UI components such as ToastNotifier or
 * InlineAlertNotifier.
 *
 * Flash messages are emitted as `CustomEvent` events (default: `flashEvent`)
 * and handled by `FlashListener`, which decides how to render them.
 *
 * Responsibilities:
 * - Provide a convenient API for triggering flash messages
 * - Dispatch `flashEvent` events with the correct payload
 * - Support both toast and inline notification modes
 * - Allow per-message configuration such as HTML rendering and dismiss timing
 *
 * This service is designed to be registered in the application's service
 * container so it can be reused throughout the codebase.
 *
 * Supported helper methods:
 * - success(message, options)
 * - error(message, options)
 * - warning(message, options)
 * - info(message, options)
 * - toast(message, options)
 * - inline(targetElement, message, options)
 *
 * Options:
 * - type        : notification type (success, danger, warning, info, etc.)
 * - message     : message text
 * - mode        : "toast" or "inline"
 * - allowHtml   : allow sanitized HTML in the message
 * - dismissMs   : auto-dismiss timeout override
 * - target      : DOM element for inline alerts
 *
 * Example usage:
 *
 * const flash = container.resolve('flash');
 *
 * flash.success('Operazione completata!');
 *
 * flash.error('Errore di rete');
 *
 * flash.warning('Controlla i dati inseriti');
 *
 * Example inline alert:
 *
 * const container = document.querySelector('#result');
 *
 * flash.inline(container, 'Verifica i campi obbligatori.', {
 *     type: 'warning'
 * });
 *
 * Architecture:
 *
 * Flash (helper service)
 *       │
 *       ▼
 *   flashEvent
 *       │
 *       ▼
 * FlashListener
 *    │       │
 *    │       └── InlineAlertNotifier
 *    │
 *    └────────── ToastNotifier
 *
 */
class Flash {

    constructor({
        eventName = 'flashEvent',
        defaultMode = 'toast'
    } = {}) {
        this.eventName = eventName;
        this.defaultMode = defaultMode;
    }

    dispatch({
        type = 'info',
        message = '',
        mode = null,
        allowHtml = false,
        dismissMs = null,
        target = document
    } = {}) {

        const eventTarget = target instanceof Element ? target : document;

        eventTarget.dispatchEvent(new CustomEvent(this.eventName, {
            bubbles: true,
            detail: {
                type,
                message,
                mode: mode || this.defaultMode,
                allowHtml,
                dismissMs
            }
        }));
    }

    success(message, options = {}) {
        this.dispatch({
            ...options,
            type: 'success',
            message
        });
    }

    error(message, options = {}) {
        this.dispatch({
            ...options,
            type: 'danger',
            message
        });
    }

    warning(message, options = {}) {
        this.dispatch({
            ...options,
            type: 'warning',
            message
        });
    }

    info(message, options = {}) {
        this.dispatch({
            ...options,
            type: 'info',
            message
        });
    }

    toast(message, options = {}) {
        this.dispatch({
            ...options,
            message,
            mode: 'toast'
        });
    }

    inline(target, message, options = {}) {
        this.dispatch({
            ...options,
            message,
            mode: 'inline',
            target
        });
    }

}

export default Flash;